import { EventTypes } from '@event/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '@store/store';

interface PrintingSettings {
    enableFirstPrinter: boolean;
    enableAutoPrint: boolean;
}

interface PrintingSettingsState {
    settings: Record<string, PrintingSettings>;
}

const LOCAL_STORAGE_KEY = (eventType: EventTypes, eventId: string) => `printing_settings_${eventType}-${eventId}`;

const getSettingsFromLocalStorage = (eventType: EventTypes, eventId: string): PrintingSettings => {
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY(eventType, eventId));
        if (raw) return JSON.parse(raw) as PrintingSettings;
    } catch {
        // ignore
    }
    return { enableFirstPrinter: true, enableAutoPrint: true };
};

const initialState: PrintingSettingsState = {
    settings: {},
};

const setEnableFirstPrinter = createAsyncThunk(
    'printingSettings/setEnableFirstPrinter',
    async ({ eventType, eventId, value }: { eventType: EventTypes; eventId: string; value: boolean }) => {
        const current = getSettingsFromLocalStorage(eventType, eventId);
        const updated: PrintingSettings = { ...current, enableFirstPrinter: value };
        localStorage.setItem(LOCAL_STORAGE_KEY(eventType, eventId), JSON.stringify(updated));
        return { eventId, settings: updated };
    }
);

const setEnableAutoPrint = createAsyncThunk(
    'printingSettings/setEnableAutoPrint',
    async ({ eventType, eventId, value }: { eventType: EventTypes; eventId: string; value: boolean }) => {
        const current = getSettingsFromLocalStorage(eventType, eventId);
        const updated: PrintingSettings = { ...current, enableAutoPrint: value };
        localStorage.setItem(LOCAL_STORAGE_KEY(eventType, eventId), JSON.stringify(updated));
        return { eventId, settings: updated };
    }
);

const printingSettingsSlice = createSlice({
    name: 'printingSettings',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(setEnableFirstPrinter.fulfilled, (state, action) => {
                state.settings[`${action.meta.arg.eventType}-${action.payload.eventId}`] = action.payload.settings;
            })
            .addCase(setEnableAutoPrint.fulfilled, (state, action) => {
                state.settings[`${action.meta.arg.eventType}-${action.payload.eventId}`] = action.payload.settings;
            });
    },
});

const selectPrintingSettings = (eventType: EventTypes, eventId: string) => (state: RootState) =>
    state.printingSettings.settings[`${eventType}-${eventId}`] ?? getSettingsFromLocalStorage(eventType, eventId);

export { selectPrintingSettings, setEnableAutoPrint, setEnableFirstPrinter };
export default printingSettingsSlice.reducer;
